import type { CancelGoodsDisposition, FulfillmentOrderLine } from '@/infrastructure/FulfillmentAdminClient';
import { canCancelLine } from '@/utils/fulfillmentLineStatus';

/**
 * What a cancel does with each line's goods (SSD-7643, SSD-7771). Existing inventory is determined only by its
 * sourcing route and goes back to stock, so it is said, not asked. A JIT line is purchased only with a purchase order
 * or an arrival on record; it needs a Return or Keep choice. A line without either has nothing to decide.
 */
export type CancelGoodsKind = 'EXISTING' | 'BOUGHT' | 'NOT_BOUGHT';
/** `NOT_SHIPPED`: the seller never shipped a bought line (SSD-7906); offered only where `notShippable` is set. */
export type CancelGoodsChoice = 'RETURN' | 'KEEP' | 'NOT_SHIPPED';

export interface CancelGoodsLine {
  lineNo: number;
  sku: string;
  name: string;
  quantity: number;
  kind: CancelGoodsKind;
  /** Whether an arrival is on record, which determines Return's disposition. */
  arrived: boolean;
  /**
   * Whether "Not shipped by seller" can be picked (SSD-7906): a JIT line bought (purchase order or inbound waybill) that
   * has not arrived. The server refuses it on an arrived line rather than turning it into a write-off.
   */
  notShippable: boolean;
}

const EXISTING_INVENTORY = 'EXISTING_INVENTORY';
/** The server's arrived states plus outbounded; a line in any of them has goods that were shipped (SSD-7906). */
const ARRIVED_STATUSES: readonly string[] = ['INBOUNDED', 'READY', 'OUTBOUNDED'];
const hasArrived = (line: FulfillmentOrderLine): boolean =>
  line.inboundReversible === true || (line.locations ?? []).some((location) => location.qty > 0);

export type { CancelGoodsDisposition } from '@/infrastructure/FulfillmentAdminClient';

const kindOf = (line: FulfillmentOrderLine): CancelGoodsKind => {
  if (line.sourcingRoute === EXISTING_INVENTORY) {
    return 'EXISTING';
  }
  // Preserve dev's outbound cancel: the parcel is here, and its separate outbound cancel books the choice.
  if (line.lineStatus === 'OUTBOUNDED') {
    return 'BOUGHT';
  }
  return hasArrived(line) || Boolean(line.purchaseOrderNumber?.trim()) || Boolean(line.lineTrackingNumber?.trim())
    ? 'BOUGHT'
    : 'NOT_BOUGHT';
};

/**
 * The lines the cancel still cancels, each with what can be decided for its goods. `outbounded` adds the outbounded
 * lines, for the cancel that takes the outbound back first (SSD-7676): their goods are in a parcel still with us.
 */
export const cancelGoodsLinesOf = (
  lines: readonly FulfillmentOrderLine[],
  {
    outbounded = false,
    inboundTrackingLines,
  }: {
    outbounded?: boolean;
    /**
     * The lines with an INBOUND waybill row on the order (SSD-7906): the server judges "bought" by a purchase order
     * number or that row, not by the line's waybill field, so Not shipped by seller is offered on the same lines.
     */
    inboundTrackingLines?: ReadonlySet<number>;
  } = {},
): CancelGoodsLine[] =>
  lines
    .filter((line) => canCancelLine(line.lineStatus) || (outbounded && line.lineStatus === 'OUTBOUNDED'))
    .map((line) => {
      const kind = kindOf(line);
      const arrived = line.lineStatus === 'OUTBOUNDED' || hasArrived(line);
      return {
        lineNo: line.lineNo,
        sku: line.sku,
        name: line.productName ?? '',
        quantity: line.totalQuantity,
        kind,
        arrived,
        // [SSD-7906] Only the order route's per-line cancel carries it; the outbound cancel books Return or Keep only.
        notShippable:
          !outbounded &&
          kind === 'BOUGHT' &&
          (Boolean(line.purchaseOrderNumber?.trim()) || inboundTrackingLines?.has(line.lineNo) === true) &&
          !arrived &&
          line.sourcingRoute === 'JIT' &&
          !ARRIVED_STATUSES.includes(line.lineStatus),
      };
    });

/** True when every bought line has a choice. */
export const cancelGoodsAnswered = (
  lines: readonly CancelGoodsLine[],
  choices: Readonly<Partial<Record<number, CancelGoodsChoice>>>,
): boolean => lines.every((line) => line.kind !== 'BOUGHT' || choices[line.lineNo] !== undefined);

/** Require complete choices rather than silently turning an unanswered return/keep into keep. */
export const cancelGoodsDispositions = (
  lines: readonly CancelGoodsLine[],
  choices: Readonly<Partial<Record<number, CancelGoodsChoice>>>,
): CancelGoodsDisposition[] => {
  if (!cancelGoodsAnswered(lines, choices)) {
    throw new Error('Choose what to do with every purchased line');
  }
  const offLimits = lines.find((line) => choices[line.lineNo] === 'NOT_SHIPPED' && !line.notShippable);
  if (offLimits) {
    throw new Error(`Line ${offLimits.lineNo} cannot be Not shipped by seller: it has arrived or was not purchased`);
  }
  return lines.map((line) => ({
    lineNo: line.lineNo,
    receivedGoods:
      line.kind === 'NOT_BOUGHT'
        ? 'NOT_PURCHASED'
        : line.kind === 'BOUGHT' && choices[line.lineNo] === 'NOT_SHIPPED'
          ? 'NOT_SHIPPED_BY_SELLER'
          : line.kind === 'EXISTING' || choices[line.lineNo] === 'KEEP'
            ? 'KEEP_IN_STOCK'
            : line.arrived
              ? 'WRITE_OFF'
              : 'RETURN_TO_SELLER',
  }));
};

const ACTION_WORDS: Record<CancelGoodsChoice, string> = {
  RETURN: 'return to seller',
  KEEP: 'keep in stock',
  NOT_SHIPPED: 'not shipped by seller',
};

/**
 * The goods note the server keeps on the cancel and posts to the comments, one clause per line that needs action:
 * `line 1 100004607 x4: back to stock (existing inventory); line 2 100004659 x6: return to seller`. Empty when no line
 * needs any; nothing-bought lines are left out.
 */
export const describeCancelGoods = (
  lines: readonly CancelGoodsLine[],
  choices: Readonly<Partial<Record<number, CancelGoodsChoice>>>,
): string =>
  lines
    .map((line) => {
      const head = `line ${line.lineNo} ${line.sku} x${line.quantity}`;
      if (line.kind === 'EXISTING') {
        return `${head}: back to stock (existing inventory)`;
      }
      const choice = line.kind === 'BOUGHT' ? choices[line.lineNo] : undefined;
      return choice ? `${head}: ${ACTION_WORDS[choice]}` : '';
    })
    .filter((clause) => clause !== '')
    .join('; ');
